import { useEffect, useState } from "react";
import type { Rol } from "./tipos";

// Router propio por hash (§11.1: rutas por rol + guard /admin/*). Ligero, offline y sin config.
//
// La NAVEGACIÓN (IA) del panel se agrupa en SECCIONES (Hoy · Casos · Ventas y caja · Clientes ·
// Inventario · Compras · Catálogo — Ajustes · Mapa) que compone el Sidebar. Orden del tramo 03 de la
// cadena huayruro-orden-y-mapa (decisión de Kevin del 27-sep-2026): dos niveles y no más — ítem del
// menú → píldora o pestaña —, una pantalla con un solo nombre, y ningún hash cambia.
// Las páginas siguen existiendo y son alcanzables por su hash (no se pierde ninguna ruta lógica);
// cada sección apunta a una ruta primaria y "posee" (owns) las sub-rutas que la resaltan.
// El POS/Mostrador queda FUERA de estas secciones (mobile/touch-first, ver Layout).

export type RutaId =
  | "mostrador"
  | "recepcion"
  | "clientes"
  | "reposiciones"
  | "sugerencias"
  | "inventario"
  | "caja"
  | "dashboard"
  | "usuarios"
  | "sucursales"
  | "catalogo"
  | "importar-catalogo"
  | "catalogo-prueba"
  | "proveedores"
  | "pedido"
  | "recepciones-pendientes"
  | "grabadores"
  | "audio-calidad"
  | "casos"
  | "conteo"
  | "faltantes"
  | "consolidado"
  | "hoy"
  | "ajustes"
  | "mapa"
  | "por-vencer"
  | "copiloto"
  | "reconocimiento"
  | "stock-bajo"
  | "ingreso"
  | "mi-cuenta"
  | "examen"
  | "aprendizaje";

export type Vista = { id: RutaId; hash: string; label: string; icono: string; roles: Rol[]; grupo: "pos" | "admin" };

const TODOS: Rol[] = ["operador", "admin_sucursal", "super_admin", "lector_reportes"];
const OPERA: Rol[] = ["operador", "admin_sucursal", "super_admin"];
const ADMIN: Rol[] = ["admin_sucursal", "super_admin"];
const PANEL: Rol[] = ["admin_sucursal", "super_admin", "lector_reportes"];
const SUPER: Rol[] = ["super_admin"];
// Mostrador y Recepción son de quien atiende en UNA botica. RC-6 (27-sep-2026) se los había quitado
// al dueño; Kevin lo revirtió el 8-oct-2026 («para no manejar dos cuentas»): el dueño entra, elige en
// qué botica atiende (App lo pide antes de mostrar el Mostrador) y el server verifica esa botica.
const ATIENDEN: Rol[] = ["operador", "admin_sucursal", "super_admin"];

// Registro de páginas (guard por rol + navegación por hash). Se conservan TODAS las del build.
// Nombres (R-F, Kevin 27-sep-2026): una pantalla, un nombre — el del menú, el del título y el del Mapa.
export const VISTAS: Vista[] = [
  { id: "hoy", hash: "#/hoy", label: "Hoy", icono: "🏠", roles: PANEL, grupo: "admin" },
  { id: "mostrador", hash: "#/", label: "Mostrador", icono: "🧾", roles: ATIENDEN, grupo: "pos" },
  { id: "recepcion", hash: "#/recepcion", label: "Recepción", icono: "📦", roles: ATIENDEN, grupo: "pos" },
  // Clientes es ADMIN: `lector_reportes` no entra al padrón (DNI, alergias y notas son datos
  // personales y de salud) y desde RC-7 (Kevin, 27-sep-2026) el vendedor tampoco lista el padrón
  // completo — busca, crea y ve la ficha desde el Mostrador (👤 Cliente, 🗓️ Seguimiento).
  { id: "clientes", hash: "#/clientes", label: "Clientes", icono: "👤", roles: ADMIN, grupo: "admin" },
  // Clientes por cara (Kevin, 19-oct-2026; tramo 10, D-C7): quién entró, quién tiene la cara registrada, revocar y
  // exportar permisos. Dueño y Encargado; el vendedor solo reconoce y registra desde el Mostrador.
  { id: "reconocimiento", hash: "#/reconocimiento", label: "Reconocimiento", icono: "🙂", roles: ADMIN, grupo: "admin" },
  // A2: la bandeja de los clientes a los que hay que escribirles («Avisos», P11 del 27-sep-2026; antes
  // «Reposición»). Mismo alcance que el mostrador (OPERA, sin lector): son nombres y teléfonos.
  // Va en el grupo POS y no en el panel a propósito: el WhatsApp de la botica vive en un CELULAR, y
  // el shell de escritorio tiene un ancho mínimo de 1200 px que obligaría a scrollear en horizontal
  // justo para tocar "Abrir WhatsApp". El dueño y el encargado llegan por "Punto de venta" en el
  // sidebar (y desde Clientes).
  { id: "reposiciones", hash: "#/reposiciones", label: "Avisos", icono: "💊", roles: OPERA, grupo: "pos" },
  // A4: curar los consejos del mostrador es trabajo de quien manda en la botica, no de quien atiende
  // (el vendedor los USA, no los escribe). Por eso ADMIN y no OPERA.
  { id: "sugerencias", hash: "#/sugerencias", label: "Sugerencias", icono: "💡", roles: ADMIN, grupo: "admin" },
  // Copiloto (Kevin, 16-oct-2026): preguntas y recomendaciones de uso del panel del mostrador.
  { id: "copiloto", hash: "#/copiloto", label: "Copiloto", icono: "🧭", roles: ADMIN, grupo: "admin" },
  { id: "inventario", hash: "#/inventario", label: "Inventario", icono: "🗃️", roles: TODOS, grupo: "pos" },
  { id: "caja", hash: "#/caja", label: "Caja", icono: "💰", roles: TODOS, grupo: "pos" },
  { id: "dashboard", hash: "#/dashboard", label: "Ventas y caja", icono: "📊", roles: PANEL, grupo: "admin" },
  { id: "casos", hash: "#/casos", label: "Casos", icono: "🚩", roles: ADMIN, grupo: "admin" },
  // Pestañas de Inventario con dirección propia: el hash abre Inventario con esa pestaña marcada.
  // «Por vencer» y «Stock bajo» (P13, 27-sep-2026) las ven los 4 roles dentro de Inventario, así que su
  // dirección no amplía a nadie. Van en el grupo admin para no sumarse a la fila del mostrador (O-14).
  { id: "por-vencer", hash: "#/por-vencer", label: "Por vencer", icono: "⏳", roles: TODOS, grupo: "admin" },
  { id: "stock-bajo", hash: "#/stock-bajo", label: "Stock bajo", icono: "📉", roles: TODOS, grupo: "admin" },
  { id: "faltantes", hash: "#/faltantes", label: "Faltantes", icono: "⚠️", roles: ADMIN, grupo: "admin" },
  { id: "conteo", hash: "#/conteo", label: "Conteo", icono: "🔢", roles: ADMIN, grupo: "admin" },
  { id: "recepciones-pendientes", hash: "#/recepciones-pendientes", label: "Recepciones por aprobar", icono: "📸", roles: ADMIN, grupo: "admin" },
  { id: "consolidado", hash: "#/consolidado", label: "Consolidado", icono: "🏢", roles: SUPER, grupo: "admin" },
  { id: "catalogo", hash: "#/catalogo", label: "Catálogo", icono: "🏷️", roles: ADMIN, grupo: "admin" },
  { id: "importar-catalogo", hash: "#/importar-catalogo", label: "Importar catálogo", icono: "📥", roles: ADMIN, grupo: "admin" },
  // RC-3 vetado por Kevin (27-sep-2026): el encargado conserva el catálogo de prueba.
  { id: "catalogo-prueba", hash: "#/catalogo-prueba", label: "Catálogo de prueba", icono: "🧪", roles: ADMIN, grupo: "admin" },
  { id: "proveedores", hash: "#/proveedores", label: "Droguerías", icono: "🚚", roles: ADMIN, grupo: "admin" },
  { id: "pedido", hash: "#/pedido", label: "Compras", icono: "🛒", roles: ADMIN, grupo: "admin" },
  { id: "grabadores", hash: "#/grabadores", label: "Grabadores", icono: "🎙️", roles: ADMIN, grupo: "admin" },
  { id: "audio-calidad", hash: "#/audio-calidad", label: "El oído", icono: "👂", roles: ADMIN, grupo: "admin" },
  { id: "usuarios", hash: "#/usuarios", label: "Usuarios", icono: "👥", roles: ADMIN, grupo: "admin" },
  // RC-1 y RC-2 (Kevin, 27-sep-2026): vendedor y lector solo veían el nombre de su botica y un texto
  // que les pedía «actualizar aquí» sin ningún campo. El endpoint GET /sucursales sigue abierto a los
  // 4 roles porque el login lo usa (`lib/auth.ts`) y a cada uno solo le devuelve su propia botica.
  { id: "sucursales", hash: "#/sucursales", label: "Boticas", icono: "🏪", roles: ADMIN, grupo: "admin" },
  { id: "ajustes", hash: "#/ajustes", label: "Ajustes", icono: "⚙️", roles: ADMIN, grupo: "admin" },
  { id: "mapa", hash: "#/mapa", label: "Mapa", icono: "🗺️", roles: PANEL, grupo: "admin" },
  // Brief del 26-oct-2026 (Kevin): «Ingreso de inventario» dentro de Inventario para la vendedora y los de
  // arriba (el lector no carga nada); su cuenta con «Mi aprendizaje» y el examen del día, que se abren
  // desde el botón «Mi cuenta» de la Caja (no desde el panel, que ella no tiene); y las notas del equipo
  // en el panel del Dueño y del Encargado.
  { id: "ingreso", hash: "#/ingreso", label: "Ingreso", icono: "📥", roles: OPERA, grupo: "admin" },
  { id: "mi-cuenta", hash: "#/mi-cuenta", label: "Mi cuenta", icono: "🎓", roles: OPERA, grupo: "admin" },
  { id: "examen", hash: "#/examen", label: "Examen del día", icono: "📝", roles: OPERA, grupo: "admin" },
  { id: "aprendizaje", hash: "#/aprendizaje", label: "Aprendizaje", icono: "🎓", roles: ADMIN, grupo: "admin" },
];

// ---- IA nueva: las secciones del panel (dueño/encargado/lector). El POS no entra aquí. ----
export type SeccionId = "hoy" | "casos" | "ventas" | "clientes" | "inventario" | "compras" | "catalogo" | "sugerencias" | "aprendizaje" | "ajustes" | "mapa";
export type SeccionIcono = "hoy" | "casos" | "ventas" | "clientes" | "inventario" | "compras" | "catalogo" | "sugerencias" | "aprendizaje" | "ajustes" | "mapa";
export type Seccion = {
  id: SeccionId;
  label: string;
  icono: SeccionIcono;
  grupo: "cadena" | "admin";
  roles: Rol[];
  ruta: RutaId; // ruta primaria al hacer click en el ítem del sidebar
  owns: RutaId[]; // rutas cuya presencia resalta esta sección (para el estado activo)
  // Contador dinámico: casos y recepciones salen de /hoy/resumen; «mapa» (las ⭐ pendientes de la puesta
  // en marcha) sale SOLO de /mapa/estado y solo para el Dueño (OM-12 del tramo 02).
  badge?: "casos" | "recepciones" | "mapa";
};

export const SECCIONES: Seccion[] = [
  { id: "hoy", label: "Hoy", icono: "hoy", grupo: "cadena", roles: PANEL, ruta: "hoy", owns: ["hoy"] },
  { id: "casos", label: "Casos", icono: "casos", grupo: "cadena", roles: ADMIN, ruta: "casos", owns: ["casos"], badge: "casos" },
  { id: "ventas", label: "Ventas y caja", icono: "ventas", grupo: "cadena", roles: PANEL, ruta: "dashboard", owns: ["dashboard", "caja", "consolidado"] },
  // Sección propia y NO dentro de "Ventas": el padrón se consulta para atender, no para reportar —
  // y su alcance de roles es distinto (el lector no entra).
  { id: "clientes", label: "Clientes", icono: "clientes", grupo: "cadena", roles: ADMIN, ruta: "clientes", owns: ["clientes", "reconocimiento"] },
  { id: "inventario", label: "Inventario", icono: "inventario", grupo: "cadena", roles: ADMIN, ruta: "inventario", owns: ["inventario", "por-vencer", "stock-bajo", "faltantes", "conteo", "recepciones-pendientes"], badge: "recepciones" },
  { id: "compras", label: "Compras", icono: "compras", grupo: "cadena", roles: ADMIN, ruta: "pedido", owns: ["pedido", "proveedores"] },
  // Catálogo sale de Ajustes (tramo 03, R-E): es el objeto que más se toca — precios, presentaciones,
  // crónicos — y vivía a dos clics dentro de una sección de configuración. Un objeto, un hogar:
  // su carga masiva (Importar catálogo) viene con él.
  { id: "catalogo", label: "Catálogo", icono: "catalogo", grupo: "cadena", roles: ADMIN, ruta: "catalogo", owns: ["catalogo", "importar-catalogo"] },
  // Sugerencias sale de Ajustes (Kevin, 15-oct-2026, probando en VES: «no hay dónde entrar»; vivía en
  // Panel › Ajustes › «Abrir sugerencias»). Solo admin+, como antes: el `lector_reportes` no cura consejos.
  // Desde el 16-oct-2026 la sección se llama «Copiloto» y junta sus dos pantallas (píldoras): lo que el
  // panel del mostrador pregunta y recomienda, y las sugerencias de venta cruzada de la tarjeta.
  { id: "sugerencias", label: "Copiloto", icono: "sugerencias", grupo: "cadena", roles: ADMIN, ruta: "copiloto", owns: ["copiloto", "sugerencias"] },
  // Aprendizaje (brief 26-oct-2026): notas del examen diario, plan del día y fichas por aprobar.
  { id: "aprendizaje", label: "Aprendizaje", icono: "aprendizaje", grupo: "cadena", roles: ADMIN, ruta: "aprendizaje", owns: ["aprendizaje"] },
  { id: "ajustes", label: "Ajustes", icono: "ajustes", grupo: "admin", roles: ADMIN, ruta: "ajustes", owns: ["ajustes", "usuarios", "sucursales", "grabadores", "audio-calidad", "catalogo-prueba"] },
  { id: "mapa", label: "Mapa", icono: "mapa", grupo: "admin", roles: PANEL, ruta: "mapa", owns: ["mapa"], badge: "mapa" },
];

// Rótulo del primer grupo del menú: el dueño mira toda su cadena; encargado y lector, una botica.
export function etiquetaGrupoPrincipal(rol: Rol): string {
  return rol === "super_admin" ? "Tu cadena" : "Tu botica";
}

// Píldoras (R-E, dos niveles y no más): ítem del menú → píldora. Cada grupo es una sola pantalla
// para quien la usa; la píldora solo se muestra si el rol puede abrir su ruta.
export const PILDORAS: { rutas: RutaId[]; etiquetas: Partial<Record<RutaId, string>> }[] = [
  { rutas: ["dashboard", "caja", "consolidado"], etiquetas: { dashboard: "Ventas", caja: "Caja", consolidado: "Consolidado" } },
  { rutas: ["pedido", "proveedores"], etiquetas: { pedido: "Pedido", proveedores: "Droguerías" } },
  { rutas: ["copiloto", "sugerencias"], etiquetas: { copiloto: "Preguntas y recomendaciones", sugerencias: "Sugerencias" } },
  { rutas: ["clientes", "reconocimiento"], etiquetas: { clientes: "Padrón", reconocimiento: "Reconocimiento" } },
  { rutas: ["catalogo", "importar-catalogo"], etiquetas: { catalogo: "Productos", "importar-catalogo": "Importar" } },
];

export function pildorasDe(r: RutaId): (typeof PILDORAS)[number] | undefined {
  return PILDORAS.find((p) => p.rutas.includes(r));
}

// Rutas que se muestran en el shell POS (mobile/touch-first), fuera del panel de escritorio.
// «Mi cuenta» y el examen viven en el marco de la Caja para todos: la vendedora no tiene panel.
export const RUTAS_POS: RutaId[] = ["mostrador", "recepcion", "reposiciones", "mi-cuenta", "examen"];

// Las 5 pantallas del mostrador y los alias de Inventario (R-A): en pantalla angosta se pintan SIEMPRE
// en el shell del celular para quien vende o manda en la botica (el lector nunca: su Inventario va
// en el panel). Hoy el encargado tocaba «Caja» en el celular y caía al panel de 1200 px con
// «Cerrar caja» fuera de la pantalla — el error 3 de la cadena repitiéndose en su tarea diaria.
export const RUTAS_MOSTRADOR: RutaId[] = [
  ...RUTAS_POS,
  "inventario",
  "caja",
  "por-vencer",
  "stock-bajo",
  "faltantes",
  "conteo",
  "recepciones-pendientes",
];

// Pantallas del panel que ya se leen en el celular (P2 de Kevin, 27-sep-2026: primera tanda = Hoy, Casos
// y Mapa). Bajo UMBRAL_FILA_PX se pintan en el marco del celular, con «☰ Menú», en vez del panel de
// 1200 px de ancho mínimo. El resto del panel sigue en el panel (segunda tanda, sin declarar «listo»).
// Tramo 07 (O-11, tareas del día 1): Usuarios —crear la cuenta de la vendedora desde el celular— y
// Catálogo —corregir un precio desde el celular—. Mismos roles de siempre: no amplía a nadie.
// Aprendizaje (brief 26-oct-2026): las notas del equipo se miran desde el celular, como Hoy.
export const RUTAS_PANEL_CELULAR: RutaId[] = ["hoy", "casos", "mapa", "usuarios", "catalogo", "aprendizaje"];

// Grupo «Punto de venta» del panel: se arma con los roles de cada ruta, así un recorte en VISTAS
// (RC-6: el dueño sin Mostrador ni Recepción) no deja un botón muerto en el menú.
export const PUNTO_DE_VENTA: RutaId[] = ["mostrador", "recepcion", "reposiciones"];

// Pestañas de Inventario (orden del tramo 03: «Por vencer» primero porque es la única que ven los 4
// roles; la del bot al final). Cada una tiene su hash; `#/inventario` abre en «Por vencer».
export type TabInventario = "ingreso" | "porvencer" | "stockbajo" | "faltantes" | "conteo" | "recepciones";
// `sinLector`: la ven quienes atienden (vendedora, Encargado y Dueño), no el lector de reportes.
export const TABS_INVENTARIO: { id: TabInventario; ruta: RutaId; label: string; soloAdmin: boolean; sinLector?: boolean }[] = [
  // Ingreso de inventario (brief 26-oct-2026): primera porque es la tarea de la semana de la vendedora.
  { id: "ingreso", ruta: "ingreso", label: "Ingreso", soloAdmin: false, sinLector: true },
  { id: "porvencer", ruta: "por-vencer", label: "Por vencer", soloAdmin: false },
  { id: "stockbajo", ruta: "stock-bajo", label: "Stock bajo", soloAdmin: false },
  { id: "faltantes", ruta: "faltantes", label: "Faltantes", soloAdmin: true },
  { id: "conteo", ruta: "conteo", label: "Conteo", soloAdmin: true },
  { id: "recepciones", ruta: "recepciones-pendientes", label: "Por aprobar", soloAdmin: true },
];

export const RUTAS_INVENTARIO: RutaId[] = ["inventario", ...TABS_INVENTARIO.map((t) => t.ruta)];

export function tabDeRuta(r: RutaId): TabInventario {
  return TABS_INVENTARIO.find((t) => t.ruta === r)?.id ?? "porvencer";
}

export function seccionDeRuta(r: RutaId): Seccion | undefined {
  return SECCIONES.find((s) => s.owns.includes(r));
}

// ¿Este rol puede abrir esta pantalla? Misma fuente que el guard de `useRuta`: los `roles` de VISTAS.
export function puedeAbrir(rol: Rol, id: RutaId): boolean {
  return VISTAS.some((v) => v.id === id && v.roles.includes(rol));
}

// Home según rol: el vendedor arranca en el Mostrador; el panel (dueño/encargado/lector) en "Hoy".
export function rutaHome(rol: Rol): RutaId {
  return rol === "operador" ? "mostrador" : "hoy";
}

export function navegar(id: RutaId): void {
  const v = VISTAS.find((x) => x.id === id);
  if (v) window.location.hash = v.hash;
}

// Devuelve la ruta actual, validada contra el rol (si no puede o el hash está vacío, cae al home del rol).
export function useRuta(rol: Rol): RutaId {
  const [hash, setHash] = useState(() => (typeof window === "undefined" ? "" : window.location.hash));
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Hash vacío → home del rol. "#/" NO es vacío: es el Mostrador (resuelve por guard de rol abajo),
  // así que un admin/super puede abrir el Mostrador aunque su home sea "Hoy".
  const home = rutaHome(rol);
  if (!hash || hash === "#") return home;
  const vista = VISTAS.find((v) => v.hash === hash);
  if (!vista || !vista.roles.includes(rol)) return home;
  return vista.id;
}
